import { useEffect, useRef } from "react";
import { scrollHeaderIntoView } from "./utils/scrollToHeader";

const GOLD = "#AC8E66";
const BORDER = "#2a2a2a";
const TEXT = "#e8e0d4";
const MUTED = "#7a7060";
const BG = "#151515";

const STACK_ITEMS = [
  {
    id: "01",
    label: "Frontend",
    desc: "React · Angular · Flutter · TypeScript",
  },
  {
    id: "02",
    label: "3D & Visuals",
    desc: "three.js · Unity · Maya",
  },
  {
    id: "03",
    label: "Backend & System",
    desc: "Node.js · Python · System Architecture",
  },
];

export default function TechStackSection({ onToggle, isOpen = false, headerRef, autoScrollOnOpen = true }) {
  const localHeaderRef = useRef(null);
  const resolvedHeaderRef = headerRef || localHeaderRef;

  useEffect(() => {
    if (!autoScrollOnOpen || !isOpen || !resolvedHeaderRef.current) return;
    scrollHeaderIntoView(resolvedHeaderRef.current, "smooth", 8);
  }, [autoScrollOnOpen, isOpen, resolvedHeaderRef]);

  return (
    <div
      ref={resolvedHeaderRef}
      style={{
        background: BG,
        borderTop: `1px solid ${BORDER}`,
        borderBottom: `1px solid ${BORDER}`,
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        position: "relative",
        cursor: "pointer",
        scrollMarginTop: "calc(var(--navbar-height, 0px) + 12px)",
      }}
      className="techstack-section"
      role="button"
      tabIndex={0}
      aria-expanded={isOpen}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onToggle?.();
      }}
    >
      {/* Pfeil – immer ganz rechts, außerhalb des Grids */}
      <span
        style={{
          position: "absolute",
          top: "50%",
          right: "24px",
          color: GOLD,
          fontSize: "1rem",
          lineHeight: 1,
          display: "inline-block",
          pointerEvents: "none",
          transform: isOpen
            ? "translateY(-50%) rotate(90deg)"
            : "translateY(-50%) rotate(0deg)",
          transition: "transform 0.3s ease",
        }}
      >
        →
      </span>

      {/* Content grid */}
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "48px 64px 48px 32px",
          display: "grid",
          gridTemplateColumns: isOpen ? "1fr" : "1fr 1fr",
          gap: "80px",
          alignItems: "center",
        }}
        className="techstack-section-grid"
      >
        {/* Links: Label + Headline */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <span
              style={{
                border: `1px solid ${GOLD}`,
                color: GOLD,
                fontSize: "0.6rem",
                padding: "2px 8px",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              {"// Tech Stack"}
            </span>
          </div>

          <h2
            style={{
              color: TEXT,
              fontSize: "clamp(1.2rem, 2.5vw, 1.8rem)",
              fontWeight: 700,
              lineHeight: 1.2,
              margin: "0 0 16px 0",
            }}
          >
            Womit ich{" "}
            <span style={{ color: GOLD }}>arbeite.</span>
          </h2>

          <p
            style={{
              color: MUTED,
              fontSize: "0.82rem",
              lineHeight: 1.7,
              margin: "0",
            }}
          >
            Tools und Systeme aus echter Praxis —
            kein Showcase, sondern täglicher Einsatz.
          </p>
        </div>

        {/* Rechts – nur als Teaser wenn collapsed */}
        {!isOpen && (
          <div>
            {STACK_ITEMS.map((s) => (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "20px",
                  padding: "16px 0",
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <span
                  style={{
                    color: GOLD,
                    fontSize: "0.6rem",
                    letterSpacing: "0.15em",
                    minWidth: "24px",
                    flexShrink: 0,
                  }}
                >
                  {s.id}
                </span>
                <div>
                  <div
                    style={{
                      color: TEXT,
                      fontSize: "0.88rem",
                      fontWeight: 600,
                      marginBottom: "4px",
                    }}
                  >
                    {s.label}
                  </div>
                  <div style={{ color: MUTED, fontSize: "0.75rem" }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .techstack-section-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            padding-top: 40px !important;
            padding-bottom: 40px !important;
          }
        }
        .techstack-section:hover h2 {
          color: ${GOLD};
          transition: color 0.2s;
        }
      `}</style>
    </div>
  );
}
